import { Injectable, inject } from '@angular/core';
import { ChatClientService } from 'stream-chat-angular';
import { UnreadMessageService } from './unread-message.service';
import { StreamChatService } from '../../pages/main-pages/communication/stream-chat/stream-chat.service';
import { AuthenticationService } from './authentication.service';
import { NotificationService } from './notifications.service';
import { environment } from '../../environments/environment';

@Injectable( {
  providedIn: 'root',
} )
export class BackgroundUnreadService {
  private chatService = inject(ChatClientService);
  private streamChatService = inject(StreamChatService);
  private authService = inject(AuthenticationService);
  private unreadMessageService = inject(UnreadMessageService);
  private notificationService = inject(NotificationService);

  private isListening = false;

  constructor() {}

  async startListening( loggedId: string ): Promise<void> {
    if ( this.isListening ) return;
    this.isListening = true;

    try {
      if ( !this.chatService.chatClient?.userID ) {
        const token = await this.streamChatService.createChat( loggedId );
        await this.chatService.init( environment.chatApiKey, loggedId, token.token );
      }

      // Client-level listener: fires for messages in watched channels
      this.chatService.chatClient.on( 'message.new', ( event: any ) => {
        if ( event.message?.user?.id === loggedId ) return;
        const senderName = event.message?.user?.name || event.message?.user?.id || 'Someone';
        this.notificationService.triggerChatMessageNotification( senderName );
        if ( event.channel_id ) {
          const count = ( this.chatService.chatClient.channel( 'messaging', event.channel_id ) as any ).countUnread?.() ?? 1;
          this.unreadMessageService.updateUnreadCount( event.channel_id, count );
        }
      } );

      // Client-level listener: fires for messages in channels the user is a member of but NOT watching
      this.chatService.chatClient.on( 'notification.message_new', ( event: any ) => {
        if ( event.message?.user?.id === loggedId ) return;
        const senderName = event.message?.user?.name || event.message?.user?.id || 'Someone';
        this.notificationService.triggerChatMessageNotification( senderName );
      } );

      // Fetch initial unread counts for badge display
      await this.fetchUnreadCounts( loggedId );

    } catch ( error ) {
      this.isListening = false;
      console.error( 'Error starting message listener:', error );
    }
  }

  async fetchUnreadCounts( loggedId: string ): Promise<void> {
    try {
      if ( !this.chatService.chatClient?.userID ) return;

      const filters = { type: 'messaging', members: { $in: [ loggedId ] } };
      const options = { limit: 30, state: true };
      const channels = await this.chatService.chatClient.queryChannels( filters, {}, options );

      channels.forEach( channel => {
        this.unreadMessageService.updateUnreadCount( channel.id, channel.countUnread() );
      } );
    } catch ( error ) {
      console.error( 'Error fetching unread counts:', error );
    }
  }
}